import ProductList from "./ProductList";
import { handleChangeColor } from "../../../utils/helpers";
import {
  extentions,
  foodToggleValues,
  foodToggleLabels,
  periodicFrequencyOptions,
  quickDosageNoteSuggestions,
  productTypeValues,
} from "../../../utils/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBillingDetails } from "../../../redux/Thunk/Doctor/DoctorThunks";
import debounce from "lodash/debounce";
import Delete from "../../common/icons/Delete";
import PlusCircle from "../../common/icons/PlusCircle";
import Plus from "../../common/icons/Plus";
import Minus from "../../common/icons/Minus";
import toast from 'react-hot-toast';

import apiAxios from "../../../services/apiAxios";
import moment from "moment";
import { FieldArray } from "formik";

const MedicineDetail = ({
  setFieldValue,
  values,
  handleChange,
  products,
  billingData,
  setBillingData,
  readOnly,
  activeTab,
  qtyEditable = false,
}) => {
  const frequentProducts = useSelector(
    (state) => state?.product?.frequentProduct
  );
  const activeForm = useSelector((state) => state.formSwitcher?.activeForm);
  const orderById = useSelector((state) => state?.order?.orderById);

  const [activeDropdown, setActiveDropdown] = useState(null); // { index, field }
  const [eligibleReturnProducts, setEligibleReturnProducts] = useState([]);
  const [isFetchingEligible, setIsFetchingEligible] = useState(false);

  const patientPID = values?.patient?.PID || orderById?.patientDetail?.PID || orderById?.PID || "";
  const patientPhone = values?.patient?.phoneNumber || orderById?.patientDetail?.phoneNumber || "";
  const prescriptionXID = values?.XID || orderById?.XID || "";

  useEffect(() => {
    const fetchEligible = async () => {
      if (!patientPID && !patientPhone && !prescriptionXID) {
        setEligibleReturnProducts([]);
        return;
      }
      try {
        setIsFetchingEligible(true);
        const res = await apiAxios.get("/api/order/eligible-returns", {
          params: {
            pid: patientPID,
            phoneNumber: patientPhone,
            xid: prescriptionXID,
          },
        });
        if (res?.data?.eligibleProducts) {
          setEligibleReturnProducts(res.data.eligibleProducts);
        } else {
          setEligibleReturnProducts([]);
        }
      } catch (err) {
        console.error("Error fetching eligible return products:", err);
        setEligibleReturnProducts([]);
      } finally {
        setIsFetchingEligible(false);
      }
    };

    fetchEligible();
  }, [patientPID, patientPhone, prescriptionXID]);

  const checkReturnItemEligibility = useCallback((returnItem) => {
    const hasProductName = returnItem?.productName && returnItem.productName.trim().length > 0;
    const hasIID = returnItem?.IID || returnItem?.productId;

    if (!hasProductName && !hasIID) {
      return {
        isEligible: false,
        reason: "No product selected",
        status: "EMPTY"
      };
    }

    const hasPatientContext = Boolean(patientPID || patientPhone || values?.patient?.name || prescriptionXID);
    if (!hasPatientContext) {
      return {
        isEligible: false,
        reason: "No patient selected in Patient details",
        status: "NO_PATIENT"
      };
    }

    const match = (eligibleReturnProducts || []).find((p) => {
      const matchIID = hasIID && p.IID && (String(hasIID) === String(p.IID) || String(hasIID) === String(p.productId));
      const matchName = hasProductName && p.productName && 
        p.productName.trim().toLowerCase() === returnItem.productName.trim().toLowerCase();
      return matchIID || matchName;
    });

    if (match) {
      const daysAgo = Math.floor((new Date() - new Date(match.createdAt)) / (1000 * 60 * 60 * 24));
      const dateStr = moment(match.createdAt).format("DD MMM YYYY");
      const agoStr = daysAgo === 0 ? "Today" : `${daysAgo} day${daysAgo > 1 ? "s" : ""} ago`;
      return {
        isEligible: true,
        reason: `Ordered on ${dateStr} (${agoStr})`,
        status: "VERIFIED",
        matchedOrder: match
      };
    }

    return {
      isEligible: false,
      reason: "Not ordered by this patient in last 30 days",
      status: "UNVERIFIED"
    };
  }, [eligibleReturnProducts, patientPID, patientPhone, values?.patient?.name, prescriptionXID]);

  useEffect(() => {
    const returnProducts = values?.returnProducts || [];
    if (returnProducts.length === 0) {
      if (values?.hasIneligibleReturnProduct) {
        setFieldValue("hasIneligibleReturnProduct", false);
      }
      return;
    }

    let hasIneligible = false;
    for (const rp of returnProducts) {
      const eligibility = checkReturnItemEligibility(rp);
      if (!eligibility.isEligible) {
        hasIneligible = true;
        break;
      }
    }

    if (values?.hasIneligibleReturnProduct !== hasIneligible) {
      setFieldValue("hasIneligibleReturnProduct", hasIneligible);
    }
  }, [values?.returnProducts, checkReturnItemEligibility, setFieldValue, values?.hasIneligibleReturnProduct]);

  const toggleDropdown = (index, field) => {
    if (activeDropdown?.index === index && activeDropdown?.field === field) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown({ index, field });
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const dispatch = useDispatch();

  useEffect(() => {
    if (orderById?.orderProducts?.length > 0) {
      let productsData = products ? products : frequentProducts;
      const filteredProducts = productsData?.frequentProducts?.filter((pro) =>
        orderById?.orderProducts?.some((selected) => selected?.IID === pro?.IID)
      );
      const filteredSearchProducts = productsData?.searchProducts?.filter(
        (pro) =>
          orderById?.orderProducts?.some(
            (selected) => selected?.IID === pro?.IID
          )
      );
      const filteredAlternateProducts = productsData?.alternateProducts?.filter(
        (pro) =>
          orderById?.orderProducts?.some(
            (selected) => selected?.IID === pro?.IID
          )
      );
      const productList = filteredProducts?.length
        ? filteredProducts
        : filteredSearchProducts?.length
          ? filteredSearchProducts
          : filteredAlternateProducts?.length
            ? filteredAlternateProducts
            : {};
      setSelectedProducts((prev) => {
        const updatedProducts = { ...prev };
        if (Array.isArray(productList)) {
          productList.forEach((product) => {
            updatedProducts[product?.IID] = product;
          });
        }

        return updatedProducts;
      });
      const hasReturns = orderById?.orderProducts?.some(p => p.isReturn) || (values?.returnProducts && values.returnProducts.length > 0);
      setBillingData(activeForm === "Edit" && !hasReturns ? 1 : 0);
    } else {
      setSelectedProducts({});
    }
  }, [orderById, activeForm, frequentProducts, products, values?.returnProducts, setBillingData]);

  const [billingDetails, setBillingDetails] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]); // Track selected products

  const handleClickProductType = (index) => {
    const currentType = values.products[index].type || "Tablet";

    const currentTypeIndex = productTypeValues.findIndex(
      (type) => type === currentType
    );

    // Calculate the next index, wrapping around to the first element
    const nextTypeIndex = (currentTypeIndex + 1) % productTypeValues.length;
    const nextType = productTypeValues[nextTypeIndex];
    setFieldValue(`products.${index}.typeIndex`, nextTypeIndex);
    setFieldValue(`products.${index}.type`, nextType);

    const foodRelatedTypes = ["Tablet", "Capsules", "Syrup", "Suspension"];
    const isFoodRelated = foodRelatedTypes.includes(nextType);
    const defaultTakenTime = isFoodRelated ? "A/F" : "AD";
    const defaultTakenTimeIndex = isFoodRelated ? 0 : 6;
    setFieldValue(`products.${index}.takenTime`, defaultTakenTime);
    setFieldValue(`products.${index}.takenTimeIndex`, defaultTakenTimeIndex);

    // Trigger recalculation in case type has different defaults/calculations
    const updatedProduct = {
      ...values.products[index],
      type: nextType,
      takenTime: defaultTakenTime,
      takenTimeIndex: defaultTakenTimeIndex
    };
    recalculateProductMetrics(index, updatedProduct);
  };

  const handleorderQty = (e, values, index, type) => {
    const { name, value } = e.target;
    // Strip non-digits and leading zeros (except when the value is just '0')
    const cleanedValue = value.replace(/[^0-9]/g, '').replace(/^0+(?!$)/, '');
    setFieldValue(name, cleanedValue);
    calculateOrderQty(values, index, cleanedValue, type);
  };

  const isCustomProduct = (p) => {
    return (
      p.isCustom === true ||
      (p.productId && String(p.productId).startsWith("CP")) ||
      (p.IID && String(p.IID).startsWith("CP")) ||
      (p.CPID && String(p.CPID).startsWith("CP"))
    );
  };

  const handleSlotCycle = (index, field) => {
    if (readOnly) return;
    if (!handleDropdownClick(index)) return;
    const p = values?.products[index] || {};
    const type = p.type || "Tablet";
    const cycle = extentions[type]?.cycle || [0, 0.5, 1, 2, 3];
    const currentVal = Number(p[field]) || 0;

    let currentIdx = cycle.findIndex(val => Math.abs(val - currentVal) < 0.01);
    if (currentIdx === -1) {
      currentIdx = 0;
    }
    const nextIdx = (currentIdx + 1) % cycle.length;
    const nextVal = cycle[nextIdx];

    setFieldValue(`products.${index}.${field}`, nextVal);
    const updatedProduct = { ...values.products[index], [field]: nextVal };
    recalculateProductMetrics(index, updatedProduct);
  };

  const handleReducingSlotCycle = (pIdx, sIdx, field) => {
    if (readOnly) return;
    const p = values?.products[pIdx] || {};
    const steps = Array.isArray(p.reducingDoses) ? [...p.reducingDoses] : [];
    if (!steps[sIdx]) return;

    const type = p.type || "Tablet";
    const cycle = extentions[type]?.cycle || [0, 0.5, 1, 2, 3];
    const currentVal = Number(steps[sIdx][field]) || 0;

    let currentIdx = cycle.findIndex(val => Math.abs(val - currentVal) < 0.01);
    if (currentIdx === -1) {
      currentIdx = 0;
    }
    const nextIdx = (currentIdx + 1) % cycle.length;
    const nextVal = cycle[nextIdx];

    steps[sIdx] = { ...steps[sIdx], [field]: nextVal };
    setFieldValue(`products.${pIdx}.reducingDoses`, steps);
    recalculateProductMetrics(pIdx, { ...p, reducingDoses: steps });
  };

  const handleReducingDosageSelect = (pIdx, sIdx, field, qty) => {
    const p = values?.products[pIdx] || {};
    const steps = Array.isArray(p.reducingDoses) ? [...p.reducingDoses] : [];
    if (!steps[sIdx]) return;

    steps[sIdx] = { ...steps[sIdx], [field]: qty };
    setFieldValue(`products.${pIdx}.reducingDoses`, steps);
    recalculateProductMetrics(pIdx, { ...p, reducingDoses: steps });
    setActiveDropdown(null);
  };

  const handleMixingToggle = (index) => {
    if (readOnly) return;
    let value =
      (values.products[index].takenTimeIndex + 1) % foodToggleValues?.length;
    setFieldValue(`products.${index}.takenTimeIndex`, value);
    setFieldValue(`products.${index}.takenTime`, foodToggleValues[value]);
    setBillingData(0);
  };

  // const handleMixingToggleClick = (index) => {
  //   if (readOnly) return;
  //   let value = (values.products[index].extraIndex + 1) % letters?.length;
  //   setFieldValue(`products.${index}.extraIndex`, value);
  //   setFieldValue(`products.${index}.extra`, letters[value]);
  //   setBillingData(0);
  // };

  const recalculateProductMetrics = (index, updatedProduct) => {
    const p = updatedProduct || values?.products[index] || {};
    const isCustom = isCustomProduct(p);
    const mode = p.dosageMode || (p.isSOS ? "SOS" : p.isNA ? "NA" : p.isReducingDose ? "REDUCING" : p.isPeriodic ? "PERIODIC" : "REGULAR");
    const isCalcType = extentions[p.type]?.calc !== false;
    const uom = Number(p.uom || p.units || 1) || 1;

    let computedRxDays = Number(p.rxDays) || 0;
    let computedRxUnits = 0;
    let computedOrderQty = Number(p.orderQty) || 0;

    if (mode === "SOS") {
      computedRxDays = p.rxDays !== undefined && p.rxDays !== null && p.rxDays !== "" ? Number(p.rxDays) : 1;
      computedRxUnits = 1;
      if (!isCustom) {
        computedOrderQty = p.isManualOrderQty ? Math.max(0, Number(p.orderQty) || 0) : (p.orderQty !== undefined && p.orderQty !== null && p.orderQty !== "" ? Number(p.orderQty) : 1);
      } else {
        computedOrderQty = 0;
      }
    } else if (mode === "NA") {
      if (p.rxDaysDisplay === "NA" || p.rxDays === "NA") {
        computedRxDays = 0;
      }
      computedRxUnits = 0;
      if (!isCustom) {
        computedOrderQty = p.isManualOrderQty ? Math.max(0, Number(p.orderQty) || 0) : (p.orderQty !== undefined && p.orderQty !== null && p.orderQty !== "" ? Number(p.orderQty) : 1);
      } else {
        computedOrderQty = 0;
      }
    } else if (mode === "PERIODIC") {
      const freq = p.periodicFrequency || "Once a week";
      const instanceDose = Number(p.morningTime) || 1;
      let intervalDays = 7;
      if (freq === "Alternate Days") intervalDays = 2;
      else if (freq === "Twice a week") intervalDays = 3.5;
      else if (freq === "Every 3 days") intervalDays = 3;
      else if (freq === "Every 5 days") intervalDays = 5;
      else if (freq === "Every 15 days") intervalDays = 15;
      else if (freq === "Once a month") intervalDays = 30;
      else if (freq === "Single Dose" || freq === "Single Dose / Stat") intervalDays = 0;

      let daysForCalculation = computedRxDays;
      if (freq === "Once a week" || freq === "Twice a week") {
        if (computedRxDays === 30) daysForCalculation = 28;
        else if (computedRxDays === 60) daysForCalculation = 56;
        else if (computedRxDays === 90) daysForCalculation = 84;
        else if (computedRxDays === 180) daysForCalculation = 168;
        else if (computedRxDays === 270) daysForCalculation = 252;
        else if (computedRxDays === 365) daysForCalculation = 364;
      }

      const totalDoses = intervalDays > 0 ? Math.ceil((daysForCalculation || 1) / intervalDays) : 1;
      computedRxUnits = totalDoses * instanceDose;

      if (isCustom) {
        computedOrderQty = 0;
      } else if (p.isManualOrderQty) {
        computedOrderQty = Math.max(0, Number(p.orderQty) || 0);
      } else if (!isCalcType || uom <= 0) {
        computedOrderQty = 1;
      } else {
        computedOrderQty = Math.ceil(computedRxUnits / uom) || 1;
      }
    } else if (mode === "REDUCING") {
      const steps = Array.isArray(p.reducingDoses) ? p.reducingDoses : [];
      let totalDays = 0;
      let totalUnits = 0;
      steps.forEach((step) => {
        const stepDays = Number(step.rxDays) || 0;
        const dailyDose = (Number(step.morningTime) || 0) + (Number(step.midDay) || 0) + (Number(step.eveningTime) || 0) + (Number(step.night) || 0);
        totalDays += stepDays;
        totalUnits += dailyDose * stepDays;
      });
      computedRxDays = totalDays;
      computedRxUnits = totalUnits;
      if (isCustom) {
        computedOrderQty = 0;
      } else if (p.isManualOrderQty) {
        computedOrderQty = Math.max(0, Number(p.orderQty) || 0);
      } else if (!isCalcType || uom <= 0) {
        computedOrderQty = 1;
      } else {
        computedOrderQty = Math.ceil(totalUnits / uom) || 1;
      }
    } else {
      // REGULAR
      const dailyDose = (Number(p.morningTime) || 0) + (Number(p.midDay) || 0) + (Number(p.eveningTime) || 0) + (Number(p.night) || 0);
      computedRxUnits = computedRxDays * dailyDose;
      if (isCustom) {
        computedOrderQty = 0;
      } else if (p.isManualOrderQty) {
        computedOrderQty = Math.max(0, Number(p.orderQty) || 0);
      } else if (!isCalcType || uom <= 0 || dailyDose === 0) {
        computedOrderQty = 1;
      } else {
        computedOrderQty = Math.ceil(computedRxUnits / uom) || 1;
      }
    }

    const orderUnits = isCustom ? 0 : computedOrderQty * uom;
    const balanceUnits = Math.max(0, computedRxUnits - orderUnits);

    setFieldValue(`products.${index}.rxDays`, computedRxDays);
    if (mode === "REDUCING") {
      setFieldValue(`products.${index}.rxDaysDisplay`, String(computedRxDays));
    }
    setFieldValue(`products.${index}.rxUnits`, computedRxUnits);
    setFieldValue(`products.${index}.orderQty`, computedOrderQty);
    setFieldValue(`products.${index}.isManualOrderQty`, Boolean(p.isManualOrderQty));
    setFieldValue(`products.${index}.orderUnits`, orderUnits);
    setFieldValue(`products.${index}.balanceUnits`, balanceUnits);
    setBillingData(0);
  };

  const handleDosageModeChange = (index, targetMode) => {
    if (readOnly) return;
    if (!values?.products[index]?.productName) {
      toast.error("Please select a product");
      return;
    }
    const currentP = values.products[index];
    let updatedProduct = {
      ...currentP,
      dosageMode: targetMode,
      isSOS: targetMode === "SOS",
      isNA: targetMode === "NA",
      isReducingDose: targetMode === "REDUCING",
      isPeriodic: targetMode === "PERIODIC",
    };

    if (targetMode === "SOS") {
      updatedProduct.morningTime = 0;
      updatedProduct.midDay = 0;
      updatedProduct.eveningTime = 0;
      updatedProduct.night = 0;
      updatedProduct.rxDays = 1;
      updatedProduct.rxDaysDisplay = "1";
      updatedProduct.orderQty = 1;
      setFieldValue(`products.${index}.morningTime`, 0);
      setFieldValue(`products.${index}.midDay`, 0);
      setFieldValue(`products.${index}.eveningTime`, 0);
      setFieldValue(`products.${index}.night`, 0);
      setFieldValue(`products.${index}.rxDays`, 1);
      setFieldValue(`products.${index}.rxDaysDisplay`, "1");
      setFieldValue(`products.${index}.orderQty`, 1);
    } else if (targetMode === "NA") {
      updatedProduct.morningTime = 0;
      updatedProduct.midDay = 0;
      updatedProduct.eveningTime = 0;
      updatedProduct.night = 0;
      updatedProduct.orderQty = 1;
      setFieldValue(`products.${index}.morningTime`, 0);
      setFieldValue(`products.${index}.midDay`, 0);
      setFieldValue(`products.${index}.eveningTime`, 0);
      setFieldValue(`products.${index}.night`, 0);
      setFieldValue(`products.${index}.orderQty`, 1);
    } else if (targetMode === "PERIODIC") {
      const defaultFreq = currentP.periodicFrequency || "Once a week";
      updatedProduct.periodicFrequency = defaultFreq;
      const activeDose = currentP.morningTime || currentP.midDay || currentP.eveningTime || currentP.night || 1;
      updatedProduct.morningTime = activeDose;
      updatedProduct.midDay = 0;
      updatedProduct.eveningTime = 0;
      updatedProduct.night = 0;
      setFieldValue(`products.${index}.periodicFrequency`, defaultFreq);
      setFieldValue(`products.${index}.morningTime`, activeDose);
      setFieldValue(`products.${index}.midDay`, 0);
      setFieldValue(`products.${index}.eveningTime`, 0);
      setFieldValue(`products.${index}.night`, 0);
    } else if (targetMode === "REDUCING") {
      let existingSteps = Array.isArray(currentP.reducingDoses) && currentP.reducingDoses.length > 0
        ? currentP.reducingDoses
        : [
          { startDay: 1, endDay: 5, rxDays: 5, morningTime: 2, midDay: 0, eveningTime: 0, night: 2, takenTime: currentP.takenTime || "A/F" },
          { startDay: 6, endDay: 10, rxDays: 5, morningTime: 1, midDay: 0, eveningTime: 0, night: 1, takenTime: currentP.takenTime || "A/F" },
        ];
      updatedProduct.reducingDoses = existingSteps;
      setFieldValue(`products.${index}.reducingDoses`, existingSteps);
    }

    setFieldValue(`products.${index}.dosageMode`, targetMode);
    setFieldValue(`products.${index}.isSOS`, targetMode === "SOS");
    setFieldValue(`products.${index}.isNA`, targetMode === "NA");
    setFieldValue(`products.${index}.isReducingDose`, targetMode === "REDUCING");
    setFieldValue(`products.${index}.isPeriodic`, targetMode === "PERIODIC");
    recalculateProductMetrics(index, updatedProduct);
  };

  const handleApplyPreset = (index, m, d, e, n) => {
    if (readOnly) return;
    if (!values?.products[index]?.productName) {
      toast.error("Please select a product");
      return;
    }
    const p = values.products[index] || {};
    const type = p.type || "Tablet";
    const defaultDose = extentions[type]?.defaultDose !== undefined ? extentions[type].defaultDose : 1;

    const finalM = m * defaultDose;
    const finalD = d * defaultDose;
    const finalE = e * defaultDose;
    const finalN = n * defaultDose;

    const updated = {
      ...p,
      dosageMode: "REGULAR",
      isSOS: false,
      isNA: false,
      isReducingDose: false,
      morningTime: finalM,
      midDay: finalD,
      eveningTime: finalE,
      night: finalN,
    };
    setFieldValue(`products.${index}.dosageMode`, "REGULAR");
    setFieldValue(`products.${index}.isSOS`, false);
    setFieldValue(`products.${index}.isNA`, false);
    setFieldValue(`products.${index}.isReducingDose`, false);
    setFieldValue(`products.${index}.morningTime`, finalM);
    setFieldValue(`products.${index}.midDay`, finalD);
    setFieldValue(`products.${index}.eveningTime`, finalE);
    setFieldValue(`products.${index}.night`, finalN);
    recalculateProductMetrics(index, updated);
  };

  const handleDosageSelect = (index, field, qty) => {
    setFieldValue(`products.${index}.${field}`, qty);
    const updatedProduct = { ...values.products[index], [field]: qty };
    recalculateProductMetrics(index, updatedProduct);
    setActiveDropdown(null);
  };

  const handleDropdownClick = (index) => {
    if (readOnly) return false;
    if (!values?.products[index]?.productName) {
      toast.error('Please select a product');
      return false;
    }
    return true;
  };

  const validateDurationInput = (index) => {
    if (readOnly) return false;
    const p = values?.products[index];
    if (!p?.productName) {
      toast.error('Please select a product');
      return false;
    }

    const isModeValid = p?.isSOS || p?.isNA || p?.isReducingDose || p?.isPeriodic || p?.dosageMode === 'SOS' || p?.dosageMode === 'NA' || p?.dosageMode === 'REDUCING' || p?.dosageMode === 'PERIODIC';
    const hasAnyDosage =
      (p?.morningTime || 0) > 0 ||
      (p?.midDay || 0) > 0 ||
      (p?.eveningTime || 0) > 0 ||
      (p?.night || 0) > 0;

    if (!isModeValid && !hasAnyDosage) {
      toast.error('Assign the required dosage');
      return false;
    }

    return true;
  };

  const calculateRxUnits = (value, index, newRxDays, type) => {
    const updatedProduct = { ...value.products[index], rxDays: newRxDays };
    recalculateProductMetrics(index, updatedProduct);
  };

  const calculateOrderQty = (value, index, newOrderQty, type) => {
    const currentProduct = value?.products[index];
    const isCustom = isCustomProduct(currentProduct);
    if (isCustom) {
      setFieldValue(`products.${index}.orderQty`, 0);
      setFieldValue(`products.${index}.orderUnits`, 0);
      return;
    }
    const qtyNum = Math.max(0, parseInt(newOrderQty || 0, 10));
    const updatedProduct = { ...currentProduct, orderQty: qtyNum, isManualOrderQty: true };
    recalculateProductMetrics(index, updatedProduct);
  };

  const resequenceReducingSteps = (steps, changedIndex = 0, field = '', rawVal = 1) => {
    if (!Array.isArray(steps) || steps.length === 0) return [];
    const updated = steps.map(s => ({ ...s }));
    const val = Math.max(1, parseInt(rawVal, 10) || 1);

    if (field === 'startDay' && updated[changedIndex]) {
      updated[changedIndex].startDay = val;
      const dur = Math.max(1, (updated[changedIndex].endDay || val) - val + 1);
      updated[changedIndex].rxDays = dur;
      updated[changedIndex].endDay = val + dur - 1;
    } else if (field === 'endDay' && updated[changedIndex]) {
      const sDay = updated[changedIndex].startDay || 1;
      const eDay = Math.max(sDay, val);
      updated[changedIndex].endDay = eDay;
      updated[changedIndex].rxDays = eDay - sDay + 1;
    } else if (field === 'rxDays' && updated[changedIndex]) {
      const sDay = updated[changedIndex].startDay || 1;
      const dur = val;
      updated[changedIndex].rxDays = dur;
      updated[changedIndex].endDay = sDay + dur - 1;
    }

    if (updated[0].startDay === undefined || updated[0].startDay === null || updated[0].startDay <= 0) {
      updated[0].startDay = 1;
      const dur = Math.max(1, Number(updated[0].rxDays) || (updated[0].endDay - updated[0].startDay + 1) || 5);
      updated[0].rxDays = dur;
      updated[0].endDay = 1 + dur - 1;
    }

    for (let i = 0; i < updated.length; i++) {
      if (i > 0) {
        const prevEnd = updated[i - 1].endDay || 1;
        updated[i].startDay = prevEnd + 1;
        const dur = Math.max(1, Number(updated[i].rxDays) || (updated[i].endDay - updated[i].startDay + 1) || 5);
        updated[i].rxDays = dur;
        updated[i].endDay = updated[i].startDay + dur - 1;
      } else {
        const dur = Math.max(1, Number(updated[i].rxDays) || (updated[i].endDay - updated[i].startDay + 1) || 5);
        updated[i].rxDays = dur;
        updated[i].endDay = updated[i].startDay + dur - 1;
      }
    }

    return updated;
  };

  const handleAddReducingStep = (index) => {
    const currentP = values.products[index];
    const steps = Array.isArray(currentP.reducingDoses) ? [...currentP.reducingDoses] : [];
    const lastStep = steps[steps.length - 1];
    const newStart = lastStep ? Number(lastStep.endDay) + 1 : 1;
    const newEnd = newStart + 4;
    const newStep = {
      startDay: newStart,
      endDay: newEnd,
      rxDays: 5,
      morningTime: 1,
      midDay: 0,
      eveningTime: 0,
      night: 1,
      takenTime: currentP.takenTime || "A/F",
    };
    const updatedSteps = resequenceReducingSteps([...steps, newStep]);
    setFieldValue(`products.${index}.reducingDoses`, updatedSteps);
    recalculateProductMetrics(index, { ...currentP, reducingDoses: updatedSteps });
  };

  const handleRemoveReducingStep = (index, stepIdx) => {
    const currentP = values.products[index];
    const steps = Array.isArray(currentP.reducingDoses) ? [...currentP.reducingDoses] : [];
    if (steps.length <= 1) {
      toast.error("At least one step is required for reducing dose");
      return;
    }
    steps.splice(stepIdx, 1);
    const reindexed = resequenceReducingSteps(steps);
    setFieldValue(`products.${index}.reducingDoses`, reindexed);
    recalculateProductMetrics(index, { ...currentP, reducingDoses: reindexed });
  };

  const handleUpdateReducingStep = (index, stepIdx, field, val) => {
    const currentP = values.products[index];
    const steps = Array.isArray(currentP.reducingDoses) ? [...currentP.reducingDoses] : [];
    if (!steps[stepIdx]) return;

    let updatedSteps = steps;
    if (field === 'startDay' || field === 'endDay' || field === 'rxDays') {
      updatedSteps = resequenceReducingSteps(steps, stepIdx, field, val);
    } else {
      updatedSteps[stepIdx] = { ...updatedSteps[stepIdx], [field]: val };
    }

    setFieldValue(`products.${index}.reducingDoses`, updatedSteps);
    recalculateProductMetrics(index, { ...currentP, reducingDoses: updatedSteps });
  };


  const fetchBillingDetails = useCallback(async () => {
    try {
      const response = await dispatch(getBillingDetails()).unwrap();
      if (response) {
        setBillingDetails(response);
      }
    } catch (error) {
      console.error("Error fetching billing details:", error);
    }
  }, [dispatch]);

  const debouncedFetchBillingDetails = useMemo(
    () => debounce(fetchBillingDetails, 300),
    [fetchBillingDetails]
  );

  useEffect(() => {
    debouncedFetchBillingDetails();
  }, [debouncedFetchBillingDetails]);


  useEffect(() => {
    if (values?.products?.length && billingData === 0) {
      const calculateTotals = () => {
        let totalMRP = 0;
        let totalDiscountAmount = 0;
        let totalGstAmount = 0;
        let totalNetAmount = 0;
        let totalHandlingFee = 0;
        let totalSmallCartFee = 0;
        let totalDeliveryCharges = 0;
        let averageDiscountAmount = 0;
        let deliveryCharges = 0;
        let calculatedTotalDiscountAmount = 0;

        values?.products.forEach((product) => {
          let { rxDays, productName } = product;
          let mrp = Number(product.mrp) || 0;
          let gst = Number(product.gst) || 0;
          let orderQty = Number(product.orderQty) || 0;
          let discount = Number(product.discount) || 0;
          const {
            noDiscount,
            smallCartFee,
            handlingFee,
            store,
            deliveryChargesOtherState,
            deliveryChargesSameState,
            deliveryChargesSameCity,
          } = billingDetails;
          const isSameState =
            store?.address?.state?.toLowerCase() ===
            values?.delivery?.address?.state?.toLowerCase();
          const isSameCity =
            store?.address?.city?.toLowerCase() ===
            values?.delivery?.address?.city?.toLowerCase();

          if (rxDays > 0 && productName) {
            totalMRP += mrp * orderQty;

            const productDiscountAmount = ((mrp * discount) / 100).toFixed(2);
            const totalProductDiscount = orderQty * Number(productDiscountAmount);

            calculatedTotalDiscountAmount += Number(totalProductDiscount);

            const belowDiscount = Number(noDiscount?.applyBelow)
              ? Number(noDiscount?.applyBelow)
              : 0;

            const billingDiscount = parseFloat(noDiscount?.percentage)
              ? parseFloat(noDiscount?.percentage)
              : 0;

            const calculatedBillingDiscount = (
              (totalMRP * billingDiscount) /
              100
            ).toFixed(2);

            const gstAmountForProduct = (mrp * gst) / 100;
            const gstByQty = gstAmountForProduct * orderQty;
            totalGstAmount += gstByQty;

            if (totalMRP < belowDiscount && noDiscount?.enable) {
              totalDiscountAmount = Number(calculatedBillingDiscount);
            } else {
              totalDiscountAmount += Number(totalProductDiscount);
            }

            totalSmallCartFee =
              totalMRP < Number(smallCartFee?.applyBelow) &&
                smallCartFee?.enable
                ? Number(billingDetails?.smallCartFee?.charges)
                : 0;

            totalHandlingFee =
              totalMRP < Number(handlingFee?.applyBelow) && handlingFee?.enable
                ? Number(billingDetails?.handlingFee?.charges)
                : 0;

            totalDeliveryCharges =
              isSameState &&
                !isSameCity &&
                deliveryChargesSameState?.enable &&
                totalMRP < Number(deliveryChargesSameState?.applyBelow)
                ? Number(deliveryChargesSameState?.charges)
                : !isSameState &&
                  totalMRP < Number(deliveryChargesOtherState?.applyBelow) &&
                  deliveryChargesOtherState?.enable
                  ? Number(deliveryChargesOtherState?.charges)
                  : isSameState &&
                    isSameCity &&
                    deliveryChargesSameCity?.enable &&
                    totalMRP < Number(deliveryChargesSameCity?.applyBelow)
                    ? Number(deliveryChargesSameCity?.charges)
                    : 0;

            averageDiscountAmount =
              totalMRP < belowDiscount && noDiscount?.enable
                ? totalDiscountAmount
                : calculatedTotalDiscountAmount;

            const totalAdditionalCharges = totalHandlingFee + totalSmallCartFee;

            deliveryCharges =
              values?.delivery?.isAddress && values?.delivery?.address?.state
                ? isNaN(totalDeliveryCharges) || totalDeliveryCharges <= 0
                  ? 0
                  : totalDeliveryCharges
                : 0;
            const netAmountBeforeGst =
              totalMRP + totalAdditionalCharges + deliveryCharges;
            totalNetAmount = netAmountBeforeGst - averageDiscountAmount;
          }
        });

        let totalReturnAmount = 0;
        if (values?.returnProducts && values.returnProducts.length > 0) {
          values.returnProducts.forEach((rp) => {
            const rpMrp = Number(rp.mrp) || 0;
            const rpQty = Number(rp.orderQty) || 0;
            const rpDiscount = Number(rp.discount) || 0;
            const unitPrice = rpDiscount > 0 ? (rpMrp - (rpMrp * rpDiscount) / 100) : rpMrp;
            totalReturnAmount += unitPrice * rpQty;
          });
        }

        totalNetAmount = Math.max(0, totalNetAmount - totalReturnAmount);

        setFieldValue("billing.subTotal", parseFloat(totalMRP?.toFixed(2)));
        setFieldValue(
          "billing.totalDiscount",
          !isNaN(averageDiscountAmount) ? averageDiscountAmount?.toFixed(2) : 0
        );
        setFieldValue(
          "billing.totalReturnAmount",
          parseFloat(totalReturnAmount?.toFixed(2)) || 0
        );
        setFieldValue(
          "billing.gst",
          !isNaN(totalGstAmount) ? parseFloat(totalGstAmount?.toFixed(2)) : 0
        );
        setFieldValue(
          "billing.smallCartFee",
          totalSmallCartFee ? parseFloat(totalSmallCartFee.toFixed(2)) : 0
        );
        setFieldValue(
          "billing.handlingFee",
          totalHandlingFee ? parseFloat(totalHandlingFee?.toFixed(2)) : 0
        );
        setFieldValue(
          "billing.deliveryCharges",
          deliveryCharges ? deliveryCharges : 0
        );
        setFieldValue(
          "billing.payableAmount",
          parseFloat(totalNetAmount?.toFixed(2)) || 0
        );
      };

      calculateTotals();
    }
  }, [values.products, values.returnProducts, values.delivery, billingData, billingDetails, setFieldValue]);

  return (
    <div className="flex flex-col mb-2">
      <FieldArray name="products">
        {({ remove, push }) => (
          <div className="border border-primary-100 rounded-[12px] bg-white shadow-2xs overflow-visible no-scrollbar mb-3">
            {/* Common Header Row (Only visible on lg: screens where components fit in one line) */}
            <div className="hidden lg:flex items-stretch gap-0 bg-primary-50/80 border-b border-primary-100 rounded-t-[12px] text-[10.5px] font-extrabold text-primary-500 uppercase tracking-wider" style={{ borderLeft: '4px solid #CCBDBD' }}>
              <div className="lg:w-[300px] lg:min-w-[300px] lg:max-w-[300px] shrink-0 py-2.5 px-3 flex items-center border-r border-primary-100/60">Products</div>
              <div className="lg:w-[120px] lg:min-w-[121px] lg:max-w-[121px] shrink-0 py-2.5 px-3 flex items-center border-r border-primary-100/60">Dosage</div>
              <div className="lg:flex-1 lg:min-w-[210px] min-w-0 py-2.5 px-3 flex items-center border-r border-primary-100/60">Instructions</div>
              <div className="flex w-full lg:w-auto shrink-0 items-stretch divide-x divide-primary-100/60">
                <div className="lg:w-[105px] lg:min-w-[105px] lg:max-w-[105px] shrink-0 py-2.5 px-3 flex items-center">Duration</div>
                <div className="lg:w-[115px] lg:min-w-[115px] lg:max-w-[115px] shrink-0 py-2.5 px-3 flex items-center">Quantity</div>
                <div className="w-[36px] min-w-[36px] max-w-[36px] shrink-0 py-2.5 px-1 flex items-center justify-center"></div>
              </div>
            </div>

            {/* Product Rows List */}
            <div className="divide-y divide-primary-100/60">
              {values?.products?.map((product, index) => {
                const currentMode = product?.dosageMode || (product?.isSOS ? "SOS" : product?.isNA ? "NA" : product?.isReducingDose ? "REDUCING" : product?.isPeriodic ? "PERIODIC" : "REGULAR");
                const hasProduct = Boolean(product?.productName && product?.productName?.trim() !== "");

                const hasValidDosage =
                  currentMode === 'SOS' ||
                  currentMode === 'NA' ||
                  (currentMode === 'REDUCING' && Array.isArray(product?.reducingDoses) && product.reducingDoses.length > 0) ||
                  (currentMode === 'PERIODIC' && (product?.morningTime > 0 || product?.midDay > 0 || product?.eveningTime > 0 || product?.night > 0 || 1)) ||
                  (currentMode === 'REGULAR' && (product?.morningTime > 0 || product?.midDay > 0 || product?.eveningTime > 0 || product?.night > 0));

                const hasValidDuration = currentMode === 'NA' || currentMode === 'SOS' || (Number(product?.rxDays) > 0 || product?.rxDaysDisplay === 'NA');

                const hasValidQuantity = Number(product?.orderQty) > 0 || isCustomProduct(product);

                const isFullyValidated = hasProduct && hasValidDosage && hasValidDuration && hasValidQuantity;

                let borderLeftColor = '#CCBDBD'; // primary-100 (Unselected / Empty)
                let bgHoverClass = 'bg-white hover:bg-primary-50/30';

                if (hasProduct) {
                  if (isFullyValidated) {
                    borderLeftColor = '#00C732'; // success-600 (Green - Complete)
                    bgHoverClass = 'bg-success-100/20 hover:bg-success-100/30';
                  } else {
                    borderLeftColor = '#C7A600'; // yellow-500 (Yellow - Incomplete)
                    bgHoverClass = 'bg-yellow-100/20 hover:bg-yellow-100/30';
                  }
                }

                let roundedClass = "";
                if (index === 0 && index === values.products.length - 1) {
                  roundedClass = `rounded-t-[11px] lg:rounded-t-none rounded-br-[11px] ${readOnly ? "rounded-bl-[11px]" : "rounded-bl-none"}`;
                } else if (index === 0) {
                  roundedClass = "rounded-t-[11px] lg:rounded-t-none rounded-b-none";
                } else if (index === values.products.length - 1) {
                  roundedClass = `rounded-t-none rounded-br-[11px] ${readOnly ? "rounded-bl-[11px]" : "rounded-bl-none"}`;
                } else {
                  roundedClass = "rounded-none";
                }

                return (
                  <div
                    key={index}
                    style={{ borderLeft: `4px solid ${borderLeftColor}` }}
                    className={`relative flex flex-col lg:flex-row items-stretch justify-start gap-0 transition-colors duration-200 overflow-visible ${bgHoverClass} ${roundedClass}`}
                  >
                    {/* 1. Product Selection */}
                    <div className="w-full lg:w-[300px] lg:min-w-[300px] lg:max-w-[300px] shrink-0 p-2 lg:p-2.5 lg:border-r border-primary-100/60 flex flex-col justify-center">
                      <div className="flex items-center justify-between mb-1 lg:hidden">
                        <span className="text-[10px] font-bold text-primary-400 uppercase">Product Selection</span>
                        <button
                          type="button"
                          disabled={readOnly || values?.products?.length <= 1}
                          onClick={() => {
                            if (readOnly || values?.products?.length <= 1) return;
                            remove(index);
                            setBillingData(0);
                          }}
                          className={`min-h-[28px] px-2 py-0.5 rounded transition-all flex items-center gap-1 text-[11px] font-bold touch-manipulation shadow-2xs ${values?.products?.length <= 1 || readOnly
                            ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-50"
                            : "bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 active:scale-95 cursor-pointer"
                            }`}
                          title={values?.products?.length <= 1 ? "At least one product is required" : "Delete Product"}
                        >
                          <Delete width={13} height={13} fill="currentColor" />
                          <span>Delete</span>
                        </button>
                      </div>
                      <ProductList
                        name={`products.${index}.productName`}
                        unitName={`products.${index}.units`}
                        index={`products.${index}`}
                        values={product}
                        onChange={setFieldValue}
                        setFieldValue={setFieldValue}
                        setBillingData={setBillingData}
                        handleClickProductType={handleClickProductType}
                        setSelectedProducts={setSelectedProducts}
                        selectedProducts={selectedProducts}
                        allProducts={[...(values?.products || []), ...(values?.returnProducts || [])]}
                        readOnly={readOnly}
                      />
                    </div>

                    {/* Dosage Type & Instructions Wrapper for Mobile */}
                    <div className="flex flex-col sm:flex-row items-stretch w-full lg:w-auto border-t border-primary-100/60 lg:border-t-0 divide-y sm:divide-y-0 sm:divide-x divide-primary-100/60 lg:border-r lg:flex-1 lg:min-w-0 border-primary-100/60">
                      {/* 2. Dosage Type */}
                      <div className="w-full sm:w-[120px] lg:w-[120px] lg:min-w-[120px] lg:max-w-[120px] shrink-0 p-2 lg:p-2.5 lg:border-r-0 flex flex-col justify-center relative dropdown-container">
                        <span className="text-[10px] font-bold text-primary-400 uppercase mb-1 lg:hidden">Dosage Type</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (!handleDropdownClick(index)) return;
                            toggleDropdown(index, 'dosageTypeSelect');
                          }}
                          className="w-full h-[34px] lg:h-[82px] px-2 flex items-center justify-between bg-white border border-primary-100 rounded-md font-bold text-xs text-primary-700 hover:border-primary-200 transition-colors shadow-2xs"
                        >
                          <span className="truncate">
                            {currentMode === "REGULAR" && "Daily"}
                            {currentMode === "PERIODIC" && "Periodic"}
                            {currentMode === "SOS" && "SOS"}
                            {currentMode === "NA" && "NA"}
                            {currentMode === "REDUCING" && "Reducing"}
                          </span>
                          <span className="text-primary-400 ml-1.5 shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          </span>
                        </button>

                        {activeDropdown?.index === index && activeDropdown?.field === 'dosageTypeSelect' && (
                          <div className="absolute top-full left-0 right-0 mt-1 p-1 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100]">
                            {[
                              { mode: "REGULAR", label: "Daily" },
                              { mode: "PERIODIC", label: "Periodic" },
                              { mode: "SOS", label: "SOS" },
                              { mode: "NA", label: "NA" },
                              { mode: "REDUCING", label: "Reducing" },
                            ].map((item) => (
                              <button
                                key={item.mode}
                                type="button"
                                onMouseDown={() => {
                                  handleDosageModeChange(index, item.mode);
                                  setActiveDropdown(null);
                                }}
                                className={`w-full text-left px-2 py-1.5 rounded-md font-bold text-xs transition-all mb-0.5 last:mb-0 ${currentMode === item.mode ? 'bg-primary-600 text-white shadow-2xs' : 'hover:bg-primary-50 text-primary-700'}`}
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={`w-full sm:flex-1 lg:flex-1 lg:min-w-[210px] min-w-0 p-2 lg:p-2.5 lg:border-r-0 flex flex-col relative overflow-visible ${readOnly && currentMode === "REGULAR" ? "justify-center" : "justify-between"}`}>
                        {/* Header & Presets Chips Bar */}
                        {(!readOnly && currentMode === "REGULAR") ? (
                          <div className="flex items-center gap-1 w-full mb-1">
                            <span className="text-[10px] font-bold text-primary-400 uppercase shrink-0 lg:hidden">Dosage Instructions</span>
                            <div className="flex items-center justify-between gap-1 flex-1 overflow-x-auto no-scrollbar py-0.5">
                              <button type="button" onClick={() => handleApplyPreset(index, 1, 0, 0, 0)} className="flex-1 px-1 py-0.5 text-[9.5px] font-extrabold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-100 text-center transition-colors shrink-0" title="1-0-0 (Once Daily - Morning)">1-0-0</button>
                              <button type="button" onClick={() => handleApplyPreset(index, 1, 0, 0, 1)} className="flex-1 px-1 py-0.5 text-[9.5px] font-extrabold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-100 text-center transition-colors shrink-0" title="1-0-1 (Twice Daily - Morning & Night)">1-0-1</button>
                              <button type="button" onClick={() => handleApplyPreset(index, 0, 0, 0, 1)} className="flex-1 px-1 py-0.5 text-[9.5px] font-extrabold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-100 text-center transition-colors shrink-0" title="0-0-1 (Once Daily - Night)">0-0-1</button>
                              <button type="button" onClick={() => handleApplyPreset(index, 1, 1, 0, 1)} className="flex-1 px-1 py-0.5 text-[9.5px] font-extrabold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-100 text-center transition-colors shrink-0" title="1-1-1 (Thrice Daily - Morning, Midday & Night)">1-1-1</button>
                              <button type="button" onClick={() => handleApplyPreset(index, 1, 1, 1, 1)} className="flex-1 px-1 py-0.5 text-[9.5px] font-extrabold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-100 text-center transition-colors shrink-0" title="1-1-1-1 (Four Times Daily)">1-1-1-1</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-1 w-full mb-1 lg:hidden">
                            <span className="text-[10px] font-bold text-primary-400 uppercase shrink-0">
                              {currentMode === "REGULAR" ? "Dosage Instructions" : currentMode === "PERIODIC" ? "Frequency & Dosage" : currentMode === "SOS" ? "SOS Dosing" : currentMode === "NA" ? "NA Dosing" : "Tapering Schedule"}
                            </span>
                          </div>
                        )}

                        {/* For Daily (REGULAR) mode */}
                        {currentMode === "REGULAR" && (
                          <div className={`flex flex-col justify-between gap-1 w-full ${readOnly ? "my-auto h-[38px] lg:h-[44px]" : "h-full"}`}>
                            {/* 4 Clean Slots + Toggles */}
                            <div className="flex items-center gap-1 w-full h-[38px] lg:h-[44px]">
                              <div className="flex items-center flex-1 h-full rounded-md border border-primary-100 bg-white shadow-2xs">
                                {/* Morning */}
                                <div className="relative flex-1 h-full dropdown-container">
                                  <button
                                    type="button"
                                    onClick={() => handleSlotCycle(index, 'morningTime')}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (!handleDropdownClick(index)) return;
                                      toggleDropdown(index, 'morningTime');
                                    }}
                                    className={`w-full h-full flex items-center justify-center font-bold text-xs border-r border-primary-100/60 transition-colors cursor-pointer rounded-l-md ${handleChangeColor(product?.morningTime, product?.type)}`}
                                    title="Click to cycle common doses. Right-click / hold for options."
                                  >
                                    {product?.morningTime !== 0 ? <span className="text-[14px]">{product?.morningTime}</span> : <span className="text-[10px] text-primary-400 font-extrabold">Morn</span>}
                                  </button>
                                  {activeDropdown?.index === index && activeDropdown?.field === 'morningTime' && (
                                    <div className={`absolute left-0 w-[150px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[44px]'}`}>
                                      <div className="flex items-center justify-between mb-1 pb-1 border-b border-primary-100">
                                        <span className="text-[9.5px] font-bold text-primary-400 uppercase">Morning</span>
                                        <button type="button" onMouseDown={() => handleDosageSelect(index, 'morningTime', 0)} className="text-[9.5px] font-bold text-red-600 hover:bg-red-50 px-1 py-0.5 rounded">Clear</button>
                                      </div>
                                      <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto">
                                        {(extentions[product?.type]?.quantity || [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 15, 20, 30, 50]).map((qty) => (
                                          <button key={qty} type="button" onMouseDown={() => handleDosageSelect(index, 'morningTime', qty)} className={`py-1 text-center font-bold text-[11px] rounded transition-colors ${product?.morningTime === qty ? 'bg-primary-600 text-white' : 'bg-primary-50 hover:bg-primary-100 text-primary-700'}`}>{qty}</button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Midday */}
                                <div className="relative flex-1 h-full dropdown-container">
                                  <button
                                    type="button"
                                    onClick={() => handleSlotCycle(index, 'midDay')}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (!handleDropdownClick(index)) return;
                                      toggleDropdown(index, 'midDay');
                                    }}
                                    className={`w-full h-full flex items-center justify-center font-bold text-xs border-r border-primary-100/60 transition-colors cursor-pointer ${handleChangeColor(product?.midDay, product?.type)}`}
                                    title="Click to cycle common doses. Right-click / hold for options."
                                  >
                                    {product?.midDay !== 0 ? <span className="text-[14px]">{product?.midDay}</span> : <span className="text-[10px] text-primary-400 font-extrabold">Noon</span>}
                                  </button>
                                  {activeDropdown?.index === index && activeDropdown?.field === 'midDay' && (
                                    <div className={`absolute left-0 w-[150px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[44px]'}`}>
                                      <div className="flex items-center justify-between mb-1 pb-1 border-b border-primary-100">
                                        <span className="text-[9.5px] font-bold text-primary-400 uppercase">Midday</span>
                                        <button type="button" onMouseDown={() => handleDosageSelect(index, 'midDay', 0)} className="text-[9.5px] font-bold text-red-600 hover:bg-red-50 px-1 py-0.5 rounded">Clear</button>
                                      </div>
                                      <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto">
                                        {(extentions[product?.type]?.quantity || [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 15, 20, 30, 50]).map((qty) => (
                                          <button key={qty} type="button" onMouseDown={() => handleDosageSelect(index, 'midDay', qty)} className={`py-1 text-center font-bold text-[11px] rounded transition-colors ${product?.midDay === qty ? 'bg-primary-600 text-white' : 'bg-primary-50 hover:bg-primary-100 text-primary-700'}`}>{qty}</button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Evening */}
                                <div className="relative flex-1 h-full dropdown-container">
                                  <button
                                    type="button"
                                    onClick={() => handleSlotCycle(index, 'eveningTime')}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (!handleDropdownClick(index)) return;
                                      toggleDropdown(index, 'eveningTime');
                                    }}
                                    className={`w-full h-full flex items-center justify-center font-bold text-xs border-r border-primary-100/60 transition-colors cursor-pointer ${handleChangeColor(product?.eveningTime, product?.type)}`}
                                    title="Click to cycle common doses. Right-click / hold for options."
                                  >
                                    {product?.eveningTime !== 0 ? <span className="text-[14px]">{product?.eveningTime}</span> : <span className="text-[10px] text-primary-400 font-extrabold">Eve</span>}
                                  </button>
                                  {activeDropdown?.index === index && activeDropdown?.field === 'eveningTime' && (
                                    <div className={`absolute left-0 w-[150px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[44px]'}`}>
                                      <div className="flex items-center justify-between mb-1 pb-1 border-b border-primary-100">
                                        <span className="text-[9.5px] font-bold text-primary-400 uppercase">Evening</span>
                                        <button type="button" onMouseDown={() => handleDosageSelect(index, 'eveningTime', 0)} className="text-[9.5px] font-bold text-red-600 hover:bg-red-50 px-1 py-0.5 rounded">Clear</button>
                                      </div>
                                      <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto">
                                        {(extentions[product?.type]?.quantity || [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 15, 20, 30, 50]).map((qty) => (
                                          <button key={qty} type="button" onMouseDown={() => handleDosageSelect(index, 'eveningTime', qty)} className={`py-1 text-center font-bold text-[11px] rounded transition-colors ${product?.eveningTime === qty ? 'bg-primary-600 text-white' : 'bg-primary-50 hover:bg-primary-100 text-primary-700'}`}>{qty}</button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Night */}
                                <div className="relative flex-1 h-full dropdown-container">
                                  <button
                                    type="button"
                                    onClick={() => handleSlotCycle(index, 'night')}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (!handleDropdownClick(index)) return;
                                      toggleDropdown(index, 'night');
                                    }}
                                    className={`w-full h-full flex items-center justify-center font-bold text-xs transition-colors cursor-pointer rounded-r-md ${handleChangeColor(product?.night, product?.type)}`}
                                    title="Click to cycle common doses. Right-click / hold for options."
                                  >
                                    {product?.night !== 0 ? <span className="text-[14px]">{product?.night}</span> : <span className="text-[10px] text-primary-400 font-extrabold">Night</span>}
                                  </button>
                                  {activeDropdown?.index === index && activeDropdown?.field === 'night' && (
                                    <div className={`absolute right-0 w-[150px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[44px]'}`}>
                                      <div className="flex items-center justify-between mb-1 pb-1 border-b border-primary-100">
                                        <span className="text-[9.5px] font-bold text-primary-400 uppercase">Night</span>
                                        <button type="button" onMouseDown={() => handleDosageSelect(index, 'night', 0)} className="text-[9.5px] font-bold text-red-600 hover:bg-red-50 px-1 py-0.5 rounded">Clear</button>
                                      </div>
                                      <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto">
                                        {(extentions[product?.type]?.quantity || [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 15, 20, 30, 50]).map((qty) => (
                                          <button key={qty} type="button" onMouseDown={() => handleDosageSelect(index, 'night', qty)} className={`py-1 text-center font-bold text-[11px] rounded transition-colors ${product?.night === qty ? 'bg-primary-600 text-white' : 'bg-primary-50 hover:bg-primary-100 text-primary-700'}`}>{qty}</button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Food Toggle */}
                              <div className="relative h-full dropdown-container">
                                <button
                                  type="button"
                                  className="h-full px-2.5 min-w-[44px] flex items-center justify-center text-center text-primary-700 bg-white border border-primary-100 rounded-md font-extrabold text-[11px] hover:bg-primary-50 transition-colors shrink-0 shadow-2xs cursor-pointer"
                                  onClick={() => handleMixingToggle(index)}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'takenTime');
                                  }}
                                  title="Click to cycle timing. Right-click / hold for options."
                                >
                                  {foodToggleValues[product.takenTimeIndex || 0]}
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'takenTime' && (
                                  <div className={`absolute right-0 w-[240px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    <div className="text-[10px] font-bold text-primary-400 uppercase mb-1 pb-1 border-b border-primary-100">Food Timing</div>
                                    <div className="flex flex-col gap-0.5">
                                      {foodToggleValues.map((val) => (
                                        <button
                                          key={val}
                                          type="button"
                                          onMouseDown={() => {
                                            const valIdx = foodToggleValues.indexOf(val);
                                            setFieldValue(`products.${index}.takenTimeIndex`, valIdx);
                                            setFieldValue(`products.${index}.takenTime`, val);
                                            setBillingData(0);
                                            setActiveDropdown(null);
                                          }}
                                          className={`w-full text-left px-2 py-1.5 rounded-md font-bold text-xs transition-colors ${product?.takenTime === val ? 'bg-primary-600 text-white shadow-2xs' : 'hover:bg-primary-50 text-primary-700'}`}
                                        >
                                          {foodToggleLabels[val] || val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Dosage Note Button */}
                              <div className="relative h-full dropdown-container">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'dosageNote');
                                  }}
                                  className={`h-full px-2.5 flex items-center justify-center font-extrabold text-xs border rounded-md transition-colors shrink-0 shadow-2xs ${product?.dosageNote ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-primary-700 border-primary-100 hover:bg-primary-50'}`}
                                  title="Dosage Note"
                                >
                                  {product?.dosageNote ? "Note ✓" : "Note"}
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'dosageNote' && (
                                  <div className={`absolute right-0 w-[240px] p-2.5 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[44px]'}`}>
                                    <div className="text-[10px] font-bold text-primary-400 uppercase mb-1">Dosage Note</div>
                                    <input
                                      type="text"
                                      placeholder="e.g. With warm water, after lunch..."
                                      name={`products.${index}.dosageNote`}
                                      value={product?.dosageNote || ""}
                                      onChange={handleChange}
                                      autoFocus
                                      className="w-full h-[30px] px-2 text-xs text-primary-900 border border-primary-200 rounded-md outline-none focus:border-primary-500 mb-2"
                                    />
                                    <div className="flex flex-wrap gap-1">
                                      {quickDosageNoteSuggestions.map((note) => (
                                        <button
                                          key={note}
                                          type="button"
                                          onMouseDown={() => {
                                            setFieldValue(`products.${index}.dosageNote`, note);
                                            setActiveDropdown(null);
                                          }}
                                          className="px-1.5 py-0.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium text-[10px] rounded border border-primary-100 truncate max-w-full"
                                        >
                                          {note}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* For PERIODIC mode */}
                        {currentMode === "PERIODIC" && (
                          <div className="flex flex-col justify-center w-full h-[82px] gap-1 my-auto">
                            {/* Row 1: Frequency Select & Note */}
                            <div className="flex items-center gap-1 w-full h-[36px]">
                              {/* Frequency dropdown */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'periodicFrequency');
                                  }}
                                  className="w-full h-full px-2.5 flex items-center justify-between text-primary-700 bg-white border border-primary-100 rounded-md font-bold text-xs hover:border-primary-200 transition-colors shadow-2xs select-none cursor-pointer"
                                >
                                  <span className="truncate">{periodicFrequencyOptions.find((opt) => opt.value === product?.periodicFrequency)?.label || product?.periodicFrequency || "Periodic"}</span>
                                  <span className="text-primary-400 ml-1.5 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'periodicFrequency' && (
                                  <div className={`absolute left-0 w-[180px] p-1 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] max-h-[180px] overflow-y-auto ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    {periodicFrequencyOptions.map((opt) => (
                                      <button
                                        key={opt.value}
                                        type="button"
                                        onMouseDown={() => {
                                          setFieldValue(`products.${index}.periodicFrequency`, opt.value);
                                          const updated = { ...values.products[index], periodicFrequency: opt.value };
                                          recalculateProductMetrics(index, updated);
                                          setActiveDropdown(null);
                                        }}
                                        className={`w-full text-left px-2.5 py-1.5 font-bold text-xs rounded transition-colors ${product?.periodicFrequency === opt.value ? 'bg-primary-600 text-white' : 'hover:bg-primary-50 text-primary-700'}`}
                                      >
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Dosage Note Button */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'dosageNote');
                                  }}
                                  className={`w-full h-full px-2 flex items-center justify-between font-bold text-xs border rounded-md transition-colors shrink-0 shadow-2xs select-none cursor-pointer ${product?.dosageNote ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-primary-700 border-primary-100 hover:bg-primary-50'}`}
                                  title="Dosage Note"
                                >
                                  <span className="truncate">{product?.dosageNote ? "Note ✓" : "Note"}</span>
                                  <span className={`ml-1.5 shrink-0 ${product?.dosageNote ? 'text-primary-200' : 'text-primary-400'}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'dosageNote' && (
                                  <div className={`absolute right-0 w-[240px] p-2.5 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    <div className="text-[10px] font-bold text-primary-400 uppercase mb-1">Dosage Note</div>
                                    <input
                                      type="text"
                                      placeholder="e.g. With warm water..."
                                      name={`products.${index}.dosageNote`}
                                      value={product?.dosageNote || ""}
                                      onChange={handleChange}
                                      autoFocus
                                      className="w-full h-[30px] px-2 text-xs text-primary-900 border border-primary-200 rounded-md outline-none focus:border-primary-500 mb-2"
                                    />
                                    <div className="flex flex-wrap gap-1">
                                      {quickDosageNoteSuggestions.map((note) => (
                                        <button
                                          key={note}
                                          type="button"
                                          onMouseDown={() => {
                                            setFieldValue(`products.${index}.dosageNote`, note);
                                            setActiveDropdown(null);
                                          }}
                                          className="px-1.5 py-0.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium text-[10px] rounded border border-primary-100 truncate max-w-full"
                                        >
                                          {note}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Row 2: Dose Qty & Food Condition */}
                            <div className="flex items-center gap-1 w-full h-[36px]">
                              {/* Dose Quantity */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  onClick={() => handleSlotCycle(index, 'morningTime')}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'morningTime');
                                  }}
                                  className="w-full h-full px-2 flex items-center justify-between text-primary-700 bg-white border border-primary-100 rounded-md font-bold text-xs hover:border-primary-200 transition-colors shadow-2xs select-none cursor-pointer"
                                  title="Dose quantity per instance (Click to cycle. Right-click / hold for options)"
                                >
                                  <span className="truncate">{(product?.morningTime || product?.midDay || product?.eveningTime || product?.night || 1)} Qty</span>
                                  <span className="text-primary-400 ml-1.5 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.8" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'morningTime' && (
                                  <div className={`absolute left-0 w-[150px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    <div className="flex items-center justify-between mb-1 pb-1 border-b border-primary-100">
                                      <span className="text-[9.5px] font-bold text-primary-400 uppercase">Dose Qty</span>
                                      <button type="button" onMouseDown={() => handleDosageSelect(index, 'morningTime', 0)} className="text-[9.5px] font-bold text-red-600 hover:bg-red-50 px-1 py-0.5 rounded">Clear</button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto">
                                      {(extentions[product?.type]?.quantity || [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 15, 20, 30, 50]).map((qty) => (
                                        <button key={qty} type="button" onMouseDown={() => handleDosageSelect(index, 'morningTime', qty)} className={`py-1 text-center font-bold text-[11px] rounded transition-colors ${product?.morningTime === qty ? 'bg-primary-600 text-white' : 'bg-primary-50 hover:bg-primary-100 text-primary-700'}`}>{qty}</button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Food Condition */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  className="w-full h-full px-2 flex items-center justify-between text-primary-700 bg-white border border-primary-100 rounded-md font-bold text-xs hover:bg-primary-50 transition-colors shadow-2xs select-none cursor-pointer"
                                  onClick={() => handleMixingToggle(index)}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'takenTime');
                                  }}
                                  title="Click to cycle timing. Right-click / hold for options."
                                >
                                  <span className="truncate">{foodToggleValues[product.takenTimeIndex || 0]}</span>
                                  <span className="text-primary-400 ml-1.5 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.8" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'takenTime' && (
                                  <div className={`absolute right-0 w-[240px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    <div className="text-[10px] font-bold text-primary-400 uppercase mb-1 pb-1 border-b border-primary-100">Food Timing</div>
                                    <div className="flex flex-col gap-0.5">
                                      {foodToggleValues.map((val) => (
                                        <button
                                          key={val}
                                          type="button"
                                          onMouseDown={() => {
                                            const valIdx = foodToggleValues.indexOf(val);
                                            setFieldValue(`products.${index}.takenTimeIndex`, valIdx);
                                            setFieldValue(`products.${index}.takenTime`, val);
                                            setBillingData(0);
                                            setActiveDropdown(null);
                                          }}
                                          className={`w-full text-left px-2 py-1.5 rounded-md font-bold text-xs transition-colors ${product?.takenTime === val ? 'bg-primary-600 text-white shadow-2xs' : 'hover:bg-primary-50 text-primary-700'}`}
                                        >
                                          {foodToggleLabels[val] || val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* For SOS mode */}
                        {currentMode === "SOS" && (
                          <div className="flex flex-col justify-between w-full h-[82px] p-2 bg-primary-50/50 border border-primary-100 rounded-md text-primary-700 text-xs">
                            <span className="font-extrabold text-[11px] truncate text-primary-700">SOS Mode: Take when required as advised</span>
                            <input
                              type="text"
                              placeholder="Optional note (e.g. For pain)..."
                              name={`products.${index}.dosageNote`}
                              value={product?.dosageNote || ""}
                              onChange={handleChange}
                              readOnly={readOnly}
                              className="w-full h-[36px] px-2.5 text-xs text-primary-900 bg-white border border-primary-200 rounded-md outline-none focus:border-primary-400 font-medium shadow-2xs"
                            />
                          </div>
                        )}

                        {/* For NA mode */}
                        {currentMode === "NA" && (
                          <div className="flex flex-col justify-between w-full h-[82px] p-2 bg-primary-50/50 border border-primary-100 rounded-md text-primary-700 text-xs">
                            <span className="font-extrabold text-[11px] truncate text-primary-700">NA Mode: Non-dosed / Surgical item</span>
                            <input
                              type="text"
                              placeholder="Optional note (e.g. External use)..."
                              name={`products.${index}.dosageNote`}
                              value={product?.dosageNote || ""}
                              onChange={handleChange}
                              readOnly={readOnly}
                              className="w-full h-[36px] px-2.5 text-xs text-primary-900 bg-white border border-primary-200 rounded-md outline-none focus:border-primary-400 font-medium shadow-2xs"
                            />
                          </div>
                        )}

                        {/* For REDUCING mode */}
                        {currentMode === "REDUCING" && (
                          <div className="flex flex-col justify-center w-full h-[82px] gap-1 my-auto">
                            {/* Row 1: Header Info & Note */}
                            <div className="flex items-center gap-1 w-full h-[36px]">
                              {/* Header Info */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full">
                                <div className="w-full h-full flex items-center justify-center px-2 bg-primary-50/80 border border-primary-100/60 rounded-md text-primary-700 font-extrabold text-xs select-none cursor-default">
                                  {(product?.reducingDoses || []).length} {(product?.reducingDoses || []).length === 1 ? "step" : "steps"}
                                </div>
                              </div>

                              {/* Dosage Note Button */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'dosageNote');
                                  }}
                                  className={`h-full w-full px-2.5 flex items-center justify-between font-bold text-xs border rounded-md transition-colors shrink-0 shadow-2xs select-none cursor-pointer ${product?.dosageNote ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-primary-700 border-primary-100 hover:bg-primary-50'}`}
                                >
                                  <span className="truncate">{product?.dosageNote ? "Note ✓" : "Note"}</span>
                                  <span className={`ml-1.5 shrink-0 ${product?.dosageNote ? 'text-primary-200' : 'text-primary-400'}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'dosageNote' && (
                                  <div className={`absolute right-0 w-[240px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    <div className="text-[10px] font-bold text-primary-400 uppercase mb-1 pb-1 border-b border-primary-100">Dosage Note</div>
                                    <input
                                      type="text"
                                      value={product?.dosageNote || ""}
                                      onChange={(e) => setFieldValue(`products.${index}.dosageNote`, e.target.value)}
                                      placeholder="Add note (e.g., take with warm water)"
                                      className="w-full px-2 py-1.5 border border-primary-200 rounded-md text-xs outline-none focus:border-primary-400 font-bold"
                                    />
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {["take with warm water", "whenever required", "sos"].map((pre) => (
                                        <button
                                          key={pre}
                                          type="button"
                                          onMouseDown={() => {
                                            setFieldValue(`products.${index}.dosageNote`, pre);
                                            setActiveDropdown(null);
                                          }}
                                          className="px-1.5 py-0.5 text-[9px] font-extrabold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded border border-primary-100 shrink-0 truncate max-w-full"
                                        >
                                          {pre}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Row 2: Configure & Food Timing */}
                            <div className="flex items-center gap-1 w-full h-[36px]">
                              {/* Configure Steps */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'reducingBuilder');
                                  }}
                                  className="w-full h-full px-2.5 flex items-center justify-between font-bold text-xs bg-white text-primary-700 border border-primary-100 rounded-md hover:bg-primary-50 transition-colors shrink-0 shadow-2xs select-none cursor-pointer"
                                >
                                  <span className="truncate">Configure Steps</span>
                                  <span className="text-primary-400 ml-1.5 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'reducingBuilder' && (
                                  <div className={`absolute left-1/2 -translate-x-1/2 right-auto w-[calc(100vw-32px)] sm:w-[380px] sm:left-auto sm:right-0 sm:translate-x-0 p-3 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] max-h-[320px] overflow-y-auto ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    {/* Popover Header: Next Days / Date Range + Close (X) */}
                                    <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-primary-100 gap-3">
                                      <div className="flex items-center p-0.5 bg-primary-100/70 rounded-lg font-bold text-xs flex-1 max-w-[240px]">
                                        <button
                                          type="button"
                                          onMouseDown={() => setFieldValue(`products.${index}.reducingStepMode`, 'nextDays')}
                                          className={`flex-1 py-1.5 rounded-md transition-all text-center touch-manipulation font-extrabold ${(product?.reducingStepMode || 'nextDays') === 'nextDays'
                                            ? 'bg-white text-primary-700 shadow-2xs'
                                            : 'text-primary-500 hover:text-primary-800'
                                            }`}
                                        >
                                          Next Days
                                        </button>
                                        <button
                                          type="button"
                                          onMouseDown={() => setFieldValue(`products.${index}.reducingStepMode`, 'dayRange')}
                                          className={`flex-1 py-1.5 rounded-md transition-all text-center touch-manipulation font-extrabold ${product?.reducingStepMode === 'dayRange'
                                            ? 'bg-white text-primary-700 shadow-2xs'
                                            : 'text-primary-500 hover:text-primary-800'
                                            }`}
                                        >
                                          Date Range
                                        </button>
                                      </div>

                                      <button
                                        type="button"
                                        onMouseDown={() => setActiveDropdown(null)}
                                        className="w-8 h-8 rounded-full bg-primary-50 hover:bg-primary-100 flex items-center justify-center text-primary-500 hover:text-primary-700 font-extrabold text-sm touch-manipulation transition-colors shrink-0"
                                        title="Close"
                                      >
                                        ✕
                                      </button>
                                    </div>

                                    {(Array.isArray(product?.reducingDoses) ? product.reducingDoses : []).map((step, sIdx) => (
                                      <div key={sIdx} className="bg-primary-50 border border-primary-100 p-2 rounded-md mb-2 last:mb-0">
                                        {(product?.reducingStepMode || 'nextDays') === 'nextDays' ? (
                                          <div className="flex items-center justify-between gap-1.5 mb-1.5 text-[11px] font-bold text-primary-700">
                                            <span className="shrink-0">
                                              Step {sIdx + 1}: {sIdx === 0 ? 'First' : 'Next'}
                                            </span>
                                            <div className="flex items-center gap-1 bg-white border border-primary-200 rounded p-0.5">
                                              <button
                                                type="button"
                                                onMouseDown={() => {
                                                  const newDur = Math.max(1, (step.rxDays || 5) - 1);
                                                  handleUpdateReducingStep(index, sIdx, 'rxDays', newDur);
                                                }}
                                                className="w-5 h-5 flex items-center justify-center bg-primary-50 hover:bg-primary-100 text-primary-700 rounded font-extrabold text-xs"
                                              >
                                                -
                                              </button>
                                              <input
                                                type="number"
                                                min="1"
                                                value={step.rxDays || (step.endDay - step.startDay + 1) || 5}
                                                onChange={(e) => {
                                                  const newDur = Math.max(1, parseInt(e.target.value, 10) || 1);
                                                  handleUpdateReducingStep(index, sIdx, 'rxDays', newDur);
                                                }}
                                                className="w-8 h-5 text-center font-extrabold text-xs text-primary-700 outline-none border-none bg-transparent"
                                              />
                                              <button
                                                type="button"
                                                onMouseDown={() => {
                                                  const newDur = (step.rxDays || 5) + 1;
                                                  handleUpdateReducingStep(index, sIdx, 'rxDays', newDur);
                                                }}
                                                className="w-5 h-5 flex items-center justify-center bg-primary-50 hover:bg-primary-100 text-primary-700 rounded font-extrabold text-xs"
                                              >
                                                +
                                              </button>
                                              <span className="text-[10px] font-medium text-primary-500 pr-1">days</span>
                                            </div>

                                            <span className="text-[10px] font-semibold text-primary-500">
                                              (Days {step.startDay}–{step.endDay})
                                            </span>

                                            {product.reducingDoses.length > 1 && (
                                              <button
                                                type="button"
                                                onMouseDown={() => handleRemoveReducingStep(index, sIdx)}
                                                className="text-red-600 hover:bg-red-50 px-1.5 py-0.5 text-[10px] rounded font-bold ml-auto"
                                              >
                                                Remove
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-between gap-1.5 mb-1.5 text-[11px] font-bold text-primary-700">
                                            <span className="shrink-0">Step {sIdx + 1}: Days</span>
                                            <div className="flex items-center gap-1">
                                              <input
                                                type="number"
                                                min="1"
                                                value={step.startDay}
                                                onChange={(e) => handleUpdateReducingStep(index, sIdx, 'startDay', e.target.value)}
                                                className="w-10 h-6 text-center border border-primary-200 rounded bg-white font-extrabold text-xs text-primary-700 shadow-2xs outline-none"
                                              />
                                              <span className="text-primary-400 font-normal text-[10px]">to</span>
                                              <input
                                                type="number"
                                                min={step.startDay}
                                                value={step.endDay}
                                                onChange={(e) => handleUpdateReducingStep(index, sIdx, 'endDay', e.target.value)}
                                                className="w-10 h-6 text-center border border-primary-200 rounded bg-white font-extrabold text-xs text-primary-700 shadow-2xs outline-none"
                                              />
                                              <span className="text-[10px] font-semibold text-primary-500 ml-0.5">({step.rxDays || (step.endDay - step.startDay + 1)}d)</span>
                                            </div>
                                            {product.reducingDoses.length > 1 && (
                                              <button
                                                type="button"
                                                onMouseDown={() => handleRemoveReducingStep(index, sIdx)}
                                                className="text-red-600 hover:bg-red-50 px-1.5 py-0.5 text-[10px] rounded font-bold ml-auto"
                                              >
                                                Remove
                                              </button>
                                            )}
                                          </div>
                                        )}

                                        <div className="grid grid-cols-4 gap-1 text-center font-bold text-[10.5px]">
                                          {['morningTime', 'midDay', 'eveningTime', 'night'].map((field, fIdx) => {
                                            const labels = ['Morn', 'Noon', 'Eve', 'Night'];
                                            const val = step[field] || 0;
                                            return (
                                              <div key={field} className="flex flex-col bg-white border border-primary-100 rounded p-0.5 relative dropdown-container">
                                                <span className="text-[8px] text-primary-400 uppercase tracking-tight leading-none mb-0.5">{labels[fIdx]}</span>
                                                <button
                                                  type="button"
                                                  onClick={() => handleReducingSlotCycle(index, sIdx, field)}
                                                  onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    if (readOnly) return;
                                                    setActiveDropdown(activeDropdown?.index === index && activeDropdown?.stepIndex === sIdx && activeDropdown?.field === field ? null : { index, stepIndex: sIdx, field });
                                                  }}
                                                  className={`w-full h-[26px] flex items-center justify-center font-bold text-xs rounded transition-colors cursor-pointer ${handleChangeColor(val, product?.type)}`}
                                                  title="Click to cycle common doses. Right-click / hold for options."
                                                >
                                                  {val !== 0 ? <span className="text-xs">{val}</span> : <span className="text-[9px] text-primary-300 font-extrabold">-</span>}
                                                </button>
                                                {activeDropdown?.index === index && activeDropdown?.stepIndex === sIdx && activeDropdown?.field === field && (
                                                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 w-[130px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[150] text-left">
                                                    <div className="flex items-center justify-between mb-1 pb-1 border-b border-primary-100">
                                                      <span className="text-[9px] font-bold text-primary-400 uppercase">{labels[fIdx]}</span>
                                                      <button type="button" onMouseDown={() => handleReducingDosageSelect(index, sIdx, field, 0)} className="text-[9px] font-bold text-red-600 hover:bg-red-50 px-1 py-0.5 rounded">Clear</button>
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-1 max-h-[100px] overflow-y-auto">
                                                      {(extentions[product?.type]?.quantity || [0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10, 15, 20, 30, 50]).map((qty) => (
                                                        <button key={qty} type="button" onMouseDown={() => handleReducingDosageSelect(index, sIdx, field, qty)} className={`py-0.5 text-center font-bold text-[10px] rounded transition-colors ${val === qty ? 'bg-primary-600 text-white' : 'bg-primary-50 hover:bg-primary-100 text-primary-700'}`}>{qty}</button>
                                                      ))}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}

                                    <div className="mt-3 pt-2.5 border-t border-primary-100 flex items-center gap-2">
                                      <button
                                        type="button"
                                        onMouseDown={() => handleAddReducingStep(index)}
                                        className="flex-1 h-[38px] bg-primary-50 text-primary-700 border border-primary-100 font-extrabold text-[12px] rounded-lg hover:bg-primary-100 transition-all flex items-center justify-center gap-1 active:scale-95 touch-manipulation"
                                      >
                                        + Add Step
                                      </button>
                                      <button
                                        type="button"
                                        onMouseDown={() => setActiveDropdown(null)}
                                        className="flex-1 h-[38px] bg-primary-600 text-white font-extrabold text-[12px] rounded-lg hover:bg-primary-700 transition-all flex items-center justify-center active:scale-95 touch-manipulation shadow-2xs"
                                      >
                                        Done
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Food Timing */}
                              <div className="relative flex-1 min-w-0 w-1/2 h-full dropdown-container">
                                <button
                                  type="button"
                                  className="w-full h-full px-2 flex items-center justify-between text-left text-primary-700 bg-white border border-primary-100 rounded-md font-bold text-xs hover:bg-primary-50 transition-colors shadow-2xs select-none cursor-pointer"
                                  onClick={() => handleMixingToggle(index)}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (!handleDropdownClick(index)) return;
                                    toggleDropdown(index, 'takenTime');
                                  }}
                                  title="Click to cycle timing. Right-click / hold for options."
                                >
                                  <span className="truncate">{foodToggleValues[product.takenTimeIndex || 0]}</span>
                                  <span className="text-primary-400 ml-1.5 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.8" stroke="currentColor" className="w-[11.5px] h-[11.5px]">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                    </svg>
                                  </span>
                                </button>
                                {activeDropdown?.index === index && activeDropdown?.field === 'takenTime' && (
                                  <div className={`absolute right-0 w-[240px] p-2 bg-white border border-primary-200 rounded-xl shadow-2xl z-[100] ${index === 0 ? 'top-full mt-1' : 'bottom-[40px]'}`}>
                                    <div className="text-[10px] font-bold text-primary-400 uppercase mb-1 pb-1 border-b border-primary-100">Food Timing</div>
                                    <div className="flex flex-col gap-0.5">
                                      {foodToggleValues.map((val) => (
                                        <button
                                          key={val}
                                          type="button"
                                          onMouseDown={() => {
                                            const valIdx = foodToggleValues.indexOf(val);
                                            setFieldValue(`products.${index}.takenTimeIndex`, valIdx);
                                            setFieldValue(`products.${index}.takenTime`, val);
                                            setBillingData(0);
                                            setActiveDropdown(null);
                                          }}
                                          className={`w-full text-left px-2 py-1.5 rounded-md font-bold text-xs transition-colors ${product?.takenTime === val ? 'bg-primary-600 text-white shadow-2xs' : 'hover:bg-primary-50 text-primary-700'}`}
                                        >
                                          {foodToggleLabels[val] || val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 4. Duration & 5. Quantity & 6. Action Container */}
                    <div className="flex w-full lg:w-auto shrink-0 items-stretch divide-x divide-primary-100/60 border-t border-primary-100/60 lg:border-t-0">
                      {/* Duration */}
                      <div className="flex-1 min-w-0 lg:flex-none lg:w-[105px] lg:min-w-[105px] lg:max-w-[105px] lg:shrink-0 p-2.5 flex flex-col justify-between relative dropdown-container overflow-visible">
                        <div className="flex items-center justify-between w-full min-h-[20px] gap-1">
                          <span className="text-[10px] font-bold text-primary-400 uppercase shrink-0 lg:hidden">Duration</span>
                          {hasProduct && extentions[product?.type]?.calc !== false && (product?.rxUnits > 0 || currentMode === "SOS" || currentMode === "NA") && (
                            <span className="text-[10px] font-extrabold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded-md border border-primary-100 max-w-[85px] lg:max-w-none text-center truncate shadow-2xs">
                              {(currentMode === "SOS" || currentMode === "NA")
                                ? currentMode
                                : `${product?.rxUnits ?? 0} ${extentions[product?.type]?.extention || (Number(product?.rxUnits) === 1 ? "unit" : "units")}`}
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={(currentMode === "SOS" || currentMode === "NA")
                            ? currentMode
                            : (product?.rxDaysDisplay || (product?.rxDays !== undefined ? String(product?.rxDays) : ""))}
                          readOnly={readOnly || currentMode === "SOS" || currentMode === "NA" || currentMode === "REDUCING"}
                          onClick={() => {
                            if (currentMode === "SOS" || currentMode === "NA" || currentMode === "REDUCING") return;
                            if (!handleDropdownClick(index)) return;
                            toggleDropdown(index, 'rxDays');
                          }}
                          onChange={(e) => {
                            if (currentMode === "SOS" || currentMode === "NA" || currentMode === "REDUCING") return;
                            if (!validateDurationInput(index)) return;
                            let rawVal = e.target.value;
                            if (rawVal.toUpperCase() === "NA") {
                              setFieldValue(`products.${index}.rxDaysDisplay`, "NA");
                              setFieldValue(`products.${index}.rxDays`, 0);
                              calculateRxUnits(values, index, 0, product?.type);
                              return;
                            }
                            let val = rawVal.replace(/[^0-9]/g, '').replace(/^0+(?!$)/, '');
                            setFieldValue(`products.${index}.rxDaysDisplay`, val);
                            if (!val) {
                              setFieldValue(`products.${index}.rxDays`, 0);
                              calculateRxUnits(values, index, 0, product?.type);
                              return;
                            }
                            const num = parseInt(val, 10);
                            const unitFactor = product?.rxUnitType || 1;
                            setFieldValue(`products.${index}.rxDays`, num * unitFactor);
                            calculateRxUnits(values, index, num * unitFactor, product?.type);
                          }}
                          className={`w-full text-center text-sm lg:text-base font-extrabold text-primary-700 outline-none h-[36px] lg:h-[42px] rounded-md transition-colors shadow-2xs mt-1 ${(currentMode === "SOS" || currentMode === "NA" || currentMode === "REDUCING") ? "bg-slate-50 border-primary-50 cursor-default opacity-85 text-primary-500" : "bg-white border border-primary-100 cursor-pointer hover:border-primary-200"}`}
                        />
                        {activeDropdown?.index === index && activeDropdown?.field === 'rxDays' && (
                          <div
                            className="absolute top-full left-0 w-[200px] sm:w-[220px] lg:w-full lg:right-0 bg-white border border-primary-200 rounded-xl max-h-[220px] overflow-y-auto z-[100] shadow-2xl mt-1"
                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#CCBDBD transparent' }}
                          >
                            {[
                              { label: "NA (Not Applicable)", numeric: "NA" },
                              { label: "1 time / Single Dose", numeric: 1 },
                              { label: "3 days", numeric: 3 },
                              { label: "5 days", numeric: 5 },
                              { label: "10 days", numeric: 10 },
                              { label: "15 days", numeric: 15 },
                              { label: "1 week/7 days", numeric: 7 },
                              { label: "2 weeks/14 days", numeric: 14 },
                              { label: "3 weeks/21 days", numeric: 21 },
                              { label: "1 month", numeric: 30 },
                              { label: "2 months", numeric: 60 },
                              { label: "3 months", numeric: 90 },
                              { label: "6 months", numeric: 180 },
                              { label: "9 months", numeric: 270 },
                              { label: "1 year", numeric: 365 },
                            ].map((option) => (
                              <div
                                key={option.label}
                                onMouseDown={() => {
                                  if (!validateDurationInput(index)) return;
                                  if (option.numeric === "NA") {
                                    setFieldValue(`products.${index}.rxDaysDisplay`, "NA");
                                    setFieldValue(`products.${index}.rxDays`, 0);
                                    calculateRxUnits(values, index, 0, product?.type);
                                  } else {
                                    setFieldValue(`products.${index}.rxDaysDisplay`, option.label);
                                    setFieldValue(`products.${index}.rxDays`, option.numeric);
                                    setFieldValue(`products.${index}.rxUnitType`, 1);
                                    calculateRxUnits(values, index, option.numeric, product?.type);
                                  }
                                  setBillingData(0);
                                  setActiveDropdown(null);
                                }}
                                className="px-3 py-2 hover:bg-primary-50 cursor-pointer text-primary-700 font-bold text-xs transition-colors border-b border-primary-50 last:border-b-0 whitespace-nowrap"
                              >
                                {option.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Quantity */}
                      <div className="flex-1 min-w-0 lg:flex-none lg:w-[115px] lg:min-w-[115px] lg:max-w-[115px] lg:shrink-0 p-2.5 flex flex-col justify-between">
                        <div className="flex items-center justify-between w-full min-h-[20px] gap-1">
                          <span className="text-[10px] font-bold text-primary-400 uppercase shrink-0 lg:hidden">Quantity</span>
                          {hasProduct && product?.orderUnits > 0 && (
                            <span className="text-[10px] font-extrabold text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded-md border border-primary-100 max-w-[85px] lg:max-w-none text-center truncate shadow-2xs">
                              {product?.orderUnits ?? 0} {extentions[product?.type]?.uomExt || extentions[product?.type]?.extention || (Number(product?.orderUnits) === 1 ? "unit" : "units")}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-1 h-[36px] lg:h-[42px] rounded-md p-0.5 border border-primary-100 bg-white shadow-2xs mt-1">
                          <button
                            type="button"
                            onClick={() => {
                              if ((readOnly && !qtyEditable) || isCustomProduct(product)) return;
                              const currentQty = parseInt(product?.orderQty || 0);
                              if (currentQty > 0) {
                                const newQty = currentQty - 1;
                                setFieldValue(`products.${index}.orderQty`, newQty);
                                calculateOrderQty(values, index, newQty, product?.type);
                              }
                            }}
                            className="w-6 lg:w-7 h-full flex items-center justify-center shrink-0 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors disabled:opacity-30"
                            disabled={(readOnly && !qtyEditable) || isCustomProduct(product)}
                            title="Decrease Quantity"
                          >
                            <Minus width={12} height={12} fill="currentColor" />
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            name={`products.${index}.orderQty`}
                            value={product?.orderQty || ""}
                            onChange={(e) => {
                              if (!values?.products[index]?.productName) {
                                toast.error('Please select a product');
                                return;
                              }
                              handleorderQty(e, values, index, product?.type);
                            }}
                            readOnly={(readOnly && !qtyEditable) || isCustomProduct(product)}
                            className={`flex-1 min-w-0 text-center text-xs sm:text-sm lg:text-base font-extrabold text-primary-700 outline-none bg-transparent ${isCustomProduct(product) ? 'opacity-50' : ''}`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if ((readOnly && !qtyEditable) || isCustomProduct(product)) return;
                              const currentQty = parseInt(product?.orderQty || 0);
                              const newQty = currentQty + 1;
                              setFieldValue(`products.${index}.orderQty`, newQty);
                              calculateOrderQty(values, index, newQty, product?.type);
                            }}
                            className="w-6 lg:w-7 h-full flex items-center justify-center shrink-0 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors disabled:opacity-30"
                            disabled={(readOnly && !qtyEditable) || isCustomProduct(product)}
                            title="Increase Quantity"
                          >
                            <Plus width={12} height={12} fill="currentColor" />
                          </button>
                        </div>
                      </div>

                      {/* Action / Delete Cell (Desktop) */}
                      <button
                        type="button"
                        disabled={readOnly || values?.products?.length <= 1}
                        onClick={() => {
                          if (readOnly || values?.products?.length <= 1) return;
                          remove(index);
                          setBillingData(0);
                        }}
                        className={`hidden lg:flex w-[36px] min-w-[36px] max-w-[36px] shrink-0 h-auto self-stretch items-center justify-center transition-all ${values?.products?.length <= 1 || readOnly
                          ? "text-primary-200 cursor-not-allowed opacity-30"
                          : "text-primary-400 hover:text-red-600 hover:bg-red-500/15 cursor-pointer group"
                          }`}
                        title={values?.products?.length <= 1 ? "At least one product is required" : "Delete Medicine"}
                      >
                        <Delete width={18} height={18} fill="currentColor" className={values?.products?.length <= 1 ? "" : "transition-transform group-hover:scale-110"} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ADD PRODUCT & RETURN PRODUCT Buttons */}
            {(!readOnly || qtyEditable) && (
              <div className="p-3 bg-primary-50/50 border-t border-primary-100 rounded-b-[12px] flex flex-wrap items-center justify-center gap-3 w-full">
                <button
                  type="button"
                  onClick={() => push({
                    productName: "",
                    mrp: 0,
                    gst: 0,
                    discount: 0,
                    netAmt: 0,
                    type: "Tablet",
                    units: 1,
                    productId: null,
                    isCustom: false,
                    drugs: [],
                    uom: 1,
                    qty: 0,
                    morningTime: 0,
                    midDay: 0,
                    eveningTime: 0,
                    night: 0,
                    takenTime: "A/F",
                    takenTimeIndex: 0,
                    extra: 0,
                    extraIndex: 0,
                    rxDaysDisplay: "",
                    rxUnitType: 1,
                    rxDays: 0,
                    rxUnits: 0,
                    orderQty: 0,
                    orderUnits: 0,
                    balanceUnits: 0,
                    lookupMargin: { type: "" }
                  })}
                  className="flex items-center justify-center gap-2 w-full md:w-auto md:min-w-[220px] px-6 py-2.5 bg-white text-primary-700 hover:bg-primary-50 rounded-md font-bold text-[13px] transition-all shadow-2xs active:scale-95 border border-primary-200"
                >
                  <PlusCircle height={18} width={18} fill="currentColor" />
                  ADD PRODUCT
                </button>

                {values?.products?.some(p => p?.productName?.length > 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      const currentReturns = values?.returnProducts || [];
                      setFieldValue("returnProducts", [
                        ...currentReturns,
                        {
                          productId: null,
                          IID: null,
                          isCustom: false,
                          productName: "",
                          mrp: 0,
                          discount: 0,
                          gst: 0,
                          netAmt: 0,
                          type: "Tablet",
                          orderQty: 1,
                          units: 1,
                          isReturn: true,
                        }
                      ]);
                      setBillingData(0);
                    }}
                    className="flex items-center justify-center gap-2 w-full md:w-auto md:min-w-[220px] px-6 py-2.5 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-md font-bold text-[13px] transition-all shadow-2xs active:scale-95 border border-rose-200"
                  >
                    <Minus height={16} width={16} fill="currentColor" />
                    RETURN PRODUCT
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </FieldArray>

      {/* ═══ Return Products Block ═══ */}
      {values?.returnProducts?.length > 0 && (
        <div className="mt-3 border border-rose-200 rounded-[12px] bg-rose-50/40 shadow-2xs relative overflow-visible">
          <div className="px-4 py-2.5 bg-rose-100/70 border-b border-rose-200 rounded-t-[11px] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse"></span>
              <h4 className="text-[12px] font-extrabold text-rose-800 uppercase tracking-wider">
                Return Products ({values.returnProducts.length})
              </h4>
              {isFetchingEligible && (
                <span className="text-[10px] text-rose-600 font-semibold italic animate-pulse">
                  (Verifying 30-day patient order history...)
                </span>
              )}
            </div>
            <span className="text-[11px] font-bold text-rose-600 bg-white/80 px-2 py-0.5 rounded-full border border-rose-200">
              Subtracted in Net Amount
            </span>
          </div>

          <div className="divide-y divide-rose-100">
            {values.returnProducts.map((returnItem, rIdx) => {
              const eligibility = checkReturnItemEligibility(returnItem);
              return (
                <div
                  key={rIdx}
                  className={`p-3 bg-white hover:bg-rose-50/20 transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
                    rIdx === values.returnProducts.length - 1 && (readOnly && !qtyEditable) ? "rounded-b-[11px]" : ""
                  }`}
                >
                  {/* Product Search & Select */}
                  <div className="w-full sm:flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-extrabold text-rose-600 uppercase tracking-wider">
                          Returned Product {rIdx + 1}
                        </span>

                        {/* Leftmost 30-Day Verification Badge */}
                        {returnItem?.productName ? (
                          eligibility.isEligible ? (
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-2xs">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600 shrink-0">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              <span>Verified Return</span>
                              <span className="text-[9.5px] font-semibold text-emerald-700 opacity-90 border-l border-emerald-300 pl-1.5 ml-0.5">
                                {eligibility.reason}
                              </span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-rose-100 text-rose-900 border border-rose-300 shadow-2xs">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-rose-600 shrink-0">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                              </svg>
                              <span>Not Verified</span>
                              <span className="text-[9.5px] font-semibold text-rose-700 opacity-90 border-l border-rose-300 pl-1.5 ml-0.5">
                                {eligibility.reason}
                              </span>
                            </span>
                          )
                        ) : (
                          <span className="text-[9px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                            Select product to verify 30-day eligibility
                          </span>
                        )}
                      </div>

                      {(!readOnly || qtyEditable) && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...values.returnProducts];
                            updated.splice(rIdx, 1);
                            setFieldValue("returnProducts", updated);
                            setBillingData(0);
                          }}
                          className="sm:hidden px-2 py-0.5 rounded text-[11px] font-bold text-red-600 bg-red-50 border border-red-100 hover:bg-red-100 transition-colors flex items-center gap-1"
                        >
                          <Delete width={12} height={12} fill="currentColor" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  <ProductList
                    name={`returnProducts.${rIdx}.productName`}
                    unitName={`returnProducts.${rIdx}.units`}
                    index={`returnProducts.${rIdx}`}
                    values={returnItem}
                    onChange={setFieldValue}
                    setFieldValue={setFieldValue}
                    setBillingData={setBillingData}
                    allProducts={[...(values?.products || []), ...(values?.returnProducts || [])]}
                    readOnly={readOnly && !qtyEditable}
                    isCompact={true}
                  />
                </div>

                {/* Return Qty & Delete */}
                <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto shrink-0 pt-2 sm:pt-0 border-t sm:border-0 border-rose-100">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-rose-500 uppercase mb-1">
                      Return Quantity
                    </span>
                    <div className="flex items-center gap-1 h-[38px] rounded-md p-0.5 border border-rose-200 bg-white shadow-2xs w-[130px]">
                      <button
                        type="button"
                        onClick={() => {
                          if (readOnly && !qtyEditable) return;
                          const currentQty = parseInt(returnItem?.orderQty || 1);
                          if (currentQty > 1) {
                            setFieldValue(`returnProducts.${rIdx}.orderQty`, currentQty - 1);
                            setBillingData(0);
                          }
                        }}
                        className="w-7 h-full flex items-center justify-center shrink-0 rounded bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-30"
                        disabled={(readOnly && !qtyEditable) || (returnItem?.orderQty || 1) <= 1}
                        title="Decrease Return Quantity"
                      >
                        <Minus width={12} height={12} fill="currentColor" />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="1"
                        value={returnItem?.orderQty !== undefined && returnItem?.orderQty !== null && returnItem?.orderQty !== "" ? returnItem.orderQty : 1}
                        onChange={(e) => {
                          if (readOnly && !qtyEditable) return;
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          const num = parseInt(val, 10) || 0;
                          setFieldValue(`returnProducts.${rIdx}.orderQty`, num);
                          setBillingData(0);
                        }}
                        readOnly={readOnly && !qtyEditable}
                        className="flex-1 min-w-0 text-center text-sm font-extrabold text-rose-900 outline-none bg-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (readOnly && !qtyEditable) return;
                          const currentQty = parseInt(returnItem?.orderQty || 1, 10);
                          setFieldValue(`returnProducts.${rIdx}.orderQty`, currentQty + 1);
                          setBillingData(0);
                        }}
                        className="w-7 h-full flex items-center justify-center shrink-0 rounded bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-30"
                        disabled={readOnly && !qtyEditable}
                        title="Increase Return Quantity"
                      >
                        <Plus width={12} height={12} fill="currentColor" />
                      </button>
                    </div>
                  </div>

                  {(!readOnly || qtyEditable) && (
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...values.returnProducts];
                        updated.splice(rIdx, 1);
                        setFieldValue("returnProducts", updated);
                        setBillingData(0);
                      }}
                      className="hidden sm:flex w-9 h-9 shrink-0 items-center justify-center rounded-lg text-rose-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-100 transition-all self-end"
                      title="Delete Return Product"
                    >
                      <Delete width={16} height={16} fill="currentColor" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>

          {/* RETURN MORE PRODUCTS Button inside Return Block */}
          {(!readOnly || qtyEditable) && (
            <div className="p-3 bg-rose-100/50 border-t border-rose-200 flex justify-center w-full rounded-b-[11px]">
              <button
                type="button"
                onClick={() => {
                  const currentReturns = values?.returnProducts || [];
                  setFieldValue("returnProducts", [
                    ...currentReturns,
                    {
                      productId: null,
                      IID: null,
                      isCustom: false,
                      productName: "",
                      mrp: 0,
                      discount: 0,
                      gst: 0,
                      netAmt: 0,
                      type: "Tablet",
                      orderQty: 1,
                      units: 1,
                      isReturn: true,
                    }
                  ]);
                  setBillingData(0);
                }}
                className="flex items-center justify-center gap-2 w-full md:w-auto md:min-w-[220px] px-6 py-2 bg-white text-rose-700 hover:bg-rose-50 rounded-md font-bold text-[12px] transition-all shadow-2xs active:scale-95 border border-rose-300"
              >
                <Plus height={15} width={15} fill="currentColor" />
                RETURN MORE PRODUCTS
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MedicineDetail;
